import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';

/**
 * A sticky horizontal scrollbar for the schedule board.
 *
 * FullCalendar's own horizontal scrollbar sits at the BOTTOM of the timeline
 * scroller. On a tall day/week board that edge is far below the fold, so
 * while the user is anywhere above it there is no way to reach the board's
 * horizontal scroll (the ask behind this component: "the horizontal scrollbar
 * on the week view needs to show when at the top").
 *
 * The strip's host is `position: sticky; bottom: 0` INSIDE the schedule card,
 * so while the scroller's own bottom edge is out of view the strip pins to
 * the bottom of the scroll container (`.layout-main`), and it retreats to —
 * and hides at — its natural end-of-card spot once the native scrollbar is
 * visible again. The two never fight for the same gesture.
 *
 * The strip mirrors the scroller's `scrollLeft` in BOTH directions and keeps
 * itself in sync with FullCalendar re-renders (tab switches rebuild the
 * board DOM) via a MutationObserver over the scope, plus a ResizeObserver
 * for content-width changes.
 */
@Component({
  selector: 'app-schedule-sticky-scrollbar',
  standalone: true,
  templateUrl: './schedule-sticky-scrollbar.component.html',
  styleUrl: './schedule-sticky-scrollbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleStickyScrollbarComponent implements AfterViewInit, OnDestroy {
  /**
   * Where the scrollable targets live. Defaults to the host's parent — in
   * the schedule card that ancestor spans the FullCalendar board. Given a
   * real element the component also watches it with a MutationObserver so
   * board re-renders re-scan the targets.
   */
  @Input() scopeEl: HTMLElement | null = null;

  /** The scrollable targets (FullCalendar's scrollers). */
  @Input() targetSelector = '.fc-scroller';

  /**
   * The scroll container whose bottom edge pins the strip. Defaults to the
   * closest `.layout-main` (the app shell's vertical scroller); the window
   * viewport is the fallback. Exposed for tests.
   */
  @Input() scrollContainerEl: HTMLElement | null = null;

  /** Slack (px) for "the scroller's bottom edge is off-screen" checks. */
  @Input() offscreenSlackPx = 1;

  @ViewChild('track', { static: true }) track: ElementRef<HTMLDivElement>;

  /**
   * Host-level aria state. Applied IMPERATIVELY (not a @HostBinding): host
   * bindings are processed by the parent's change-detection pass, which this
   * OnPush component's own detectChanges() never triggers — imperative
   * toggles are deterministic everywhere (boot, board re-render, tests).
   */
  private applyHostAria(visible: boolean): void {
    const host = this.el.nativeElement;
    host.classList.toggle('visible', visible);
    if (visible) {
      host.removeAttribute('aria-hidden');
    } else {
      host.setAttribute('aria-hidden', 'true');
    }
  }

  /** Currently mirrored horizontal scrollers (may be several per board). */
  private targets: HTMLElement[] = [];

  /** true while the strip pins over the board (native scrollbar away). */
  visible = false;

  /** The targets' total scrollable width — sizes the track's scroll extent. */
  contentWidth = 1;

  /** Track alignment: matches the mirrored pane's viewport (excludes the
   *  resource-name column beside the timeline) so the strip's span and drag
   *  speed are identical to the native bar; 0/0 = full-host fallback. */
  trackLeftPx = 0;
  trackWidthPx = 0;

  private scrollContainer: HTMLElement | null = null;
  /** All scroller panes seen in the last scan (targets + candidates). */
  private candidates: HTMLElement[] = [];
  private resizeObserver?: ResizeObserver;
  /** Panes currently box-watched (ResizeObserver dedupe — see watcher). */
  private observedBoxes = new Set<HTMLElement>();
  private mutationObserver?: MutationObserver;
  private scrollContainerListener?: EventListener;
  private windowResizeListener?: EventListener;
  private targetUnbinders: Array<() => void> = [];
  /** Guards the bar ⇄ target sync against a two-scrollbar feedback echo. */
  private syncingFromBar = false;
  private echoTimer: ReturnType<typeof setTimeout> | null = null;
  private scanTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  get hostVisible(): boolean {
    return this.visible;
  }

  get hostAriaHidden(): 'true' | null {
    return this.visible ? null : 'true';
  }

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cd: ChangeDetectorRef,
  ) {}

  /** Thumb width as a % of the track's viewport, i.e. visible/total. */
  get thumbWidthPct(): number {
    return this.visibleRatio() * 100;
  }

  /** Thumb offset as a % of the track's viewport (rides 0..(100-width)). */
  get thumbLeftPct(): number {
    return this.trackScrollLeft() / this.contentWidth * 100;
  }

  /** ARIA scrollbar pair: current offset and max offset (0..max). */
  get ariaValueNow(): number {
    return this.trackScrollLeft();
  }

  get ariaValueMax(): number {
    return Math.max(0, this.contentWidth - this.trackViewWidth());
  }

  private visibleRatio(): number {
    if (this.contentWidth <= 1) { return 1; }
    return Math.min(1, this.trackViewWidth() / this.contentWidth);
  }

  private trackViewWidth(): number {
    // While the strip is pinned, the track aligns to the mirrored pane's
    // viewport (trackWidthPx) — use that so extent/thumb math is native-1:1.
    if (this.trackWidthPx > 0) {
      return this.trackWidthPx;
    }
    return this.track?.nativeElement?.clientWidth || this.el.nativeElement.clientWidth || 0;
  }

  private trackScrollLeft(): number {
    return this.track?.nativeElement?.scrollLeft || 0;
  }

  ngAfterViewInit(): void {
    this.connect();
    // The first scan must not run inside the boot change-detection pass:
    // discovering the targets can flip this host's bindings mid-check
    // (NG0100). scheduleScan defers it past CD; the strip appears one tick
    // later, which matches how the board itself settles.
    this.scheduleScan();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.observedBoxes.clear();
    this.mutationObserver?.disconnect();
    for (const unbind of this.targetUnbinders) {
      unbind();
    }
    this.targetUnbinders = [];
    if (this.scrollContainerListener) {
      this.scrollContainer?.removeEventListener('scroll', this.scrollContainerListener);
    }
    if (this.windowResizeListener) {
      window.removeEventListener('resize', this.windowResizeListener);
    }
    if (this.echoTimer !== null) {
      clearTimeout(this.echoTimer);
    }
    if (this.scanTimer !== null) {
      clearTimeout(this.scanTimer);
    }
  }

  private connect(): void {
    this.scrollContainer = this.scrollContainerEl
      ?? this.el.nativeElement.closest('.layout-main');
    // Scroll/resize fire continuously — keep them out of Angular's zone and
    // drive change detection explicitly from the handlers.
    this.zone.runOutsideAngular(() => {
      if (this.scrollContainer) {
        this.scrollContainerListener = () => this.scheduleScan();
        this.scrollContainer.addEventListener('scroll', this.scrollContainerListener, { passive: true });
      }
      this.windowResizeListener = () => this.scheduleScan();
      window.addEventListener('resize', this.windowResizeListener);
    });

    // CR af8310b6: watch the scope for board rebuilds (tab switches swap the
    // FullCalendar DOM) starting from connect() — the first scan can
    // legitimately find no overflowing target, and rebindTargets() returning
    // early must not be the only path that starts this watch.
    this.mutationObserver?.disconnect();
    const mutationScope = this.scopeEl ?? this.el.nativeElement.parentElement;
    if (mutationScope && typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => this.scheduleScan());
      this.mutationObserver.observe(mutationScope, { childList: true, subtree: true });
    }
  }

  /** Debounced rescan (scroll storms, resize bursts, board swaps). */
  private scheduleScan(): void {
    if (this.scanTimer !== null) {
      clearTimeout(this.scanTimer);
    }
    this.scanTimer = setTimeout(() => {
      this.scanTimer = null;
      if (!this.destroyed) {
        this.zone.run(() => this.scan());
      }
    }, 60);
  }

  /**
   * Re-discovers the horizontally overflowing scrollers and recomputes the
   * strip's visibility/thumb. Cheap enough to run on every board mutation.
   */
  scan(): void {
    const scope = this.scopeEl ?? this.el.nativeElement.parentElement;
    const candidates = scope
      ? Array.from(scope.querySelectorAll<HTMLElement>(this.targetSelector))
      : [];

    const nextTargets = candidates.filter((el) => this.isHorizontallyScrollable(el));
    this.candidates = candidates;
    this.rebindTargets(nextTargets);

    const master = this.pickMasterPane(nextTargets);
    if (master) {
      // Align the strip to the mirrored pane's viewport, not the whole host:
      // the resources-name column sits beside the timeline pane, so the
      // native bar's span (and pixels-per-scroll ratio) covers only the
      // pane. Matching its left/width makes drag speed and thumb geometry
      // identical to the native bar.
      const hostRect = this.el.nativeElement.getBoundingClientRect();
      this.trackLeftPx = Math.max(0, Math.round(master.getBoundingClientRect().left - hostRect.left));
      this.trackWidthPx = master.clientWidth;
    } else {
      this.trackLeftPx = 0;
      this.trackWidthPx = 0;
    }

    const maxTargetScroll = Math.max(0, ...nextTargets.map((el) => el.scrollWidth - el.clientWidth));
    this.contentWidth = Math.max(1, this.trackViewWidth() + maxTargetScroll);
    this.visible = nextTargets.length > 0 && this.targetOffscreen();
    if (this.visible) {
      // Put the strip at the targets' position only when showing it —
      // while hidden the track stays at 0 so nothing jumps later.
      this.track.nativeElement.scrollLeft = Math.max(...nextTargets.map((el) => el.scrollLeft));
    }
    this.cd.detectChanges();
    this.applyHostAria(this.visible);
  }

  /** The pane whose geometry the track mirrors: the DEEPEST target
   *  (the body timeline pane carries the native scroll bar; the header
   *  pane shares its scrollLeft). */
  private pickMasterPane(targets: HTMLElement[]): HTMLElement | null {
    return targets.reduce<HTMLElement | null>((acc, el) => {
      if (acc === null) { return el; }
      return el.getBoundingClientRect().bottom > acc.getBoundingClientRect().bottom ? el : acc;
    }, null);
  }

  private targetOffscreen(): boolean {
    const scrollerBottom = Math.max(
      ...this.targets.map((el) => el.getBoundingClientRect().bottom),
    );
    const visibleBottom = (this.scrollContainer ?? this.el.nativeElement.closest('.layout-main'))
      ? this.scrollContainer!.getBoundingClientRect().bottom
      : window.innerHeight;
    return scrollerBottom > visibleBottom + this.offscreenSlackPx;
  }

  /** A target only if it actually scrolls horizontally (overflow-x auto/scroll). */
  private isHorizontallyScrollable(el: HTMLElement): boolean {
    if (el.scrollWidth <= el.clientWidth + 1) {
      return false;
    }
    const overflowX = (el.ownerDocument.defaultView || window)
      .getComputedStyle(el)
      .overflowX;
    return overflowX === 'auto' || overflowX === 'scroll';
  }

  /** Rebind scroll listeners + observers when the target set changes. */
  private rebindTargets(nextTargets: HTMLElement[]): void {
    const sameSet =
      nextTargets.length === this.targets.length &&
      nextTargets.every((el, i) => el === this.targets[i]);
    if (sameSet) {
      // Still refresh candidate box-watchers: a pane that did not overflow
      // at scan time may start doing so (window resize / content growth)
      // without any DOM mutation — only its box change can wake us.
      this.watchCandidateBoxes(this.candidates);
      return;
    }

    for (const unbind of this.targetUnbinders) {
      unbind();
    }
    this.targetUnbinders = [];
    this.targets = nextTargets;

    this.zone.runOutsideAngular(() => {
      for (const el of this.targets) {
        const listener = () => this.onTargetScroll();
        el.addEventListener('scroll', listener, { passive: true });
        this.targetUnbinders.push(() => el.removeEventListener('scroll', listener));
      }
    });

    // One RO for every pane (candidates ⊇ targets) — covers target box
    // changes AND panes that start overflowing later (CR 3de2c80d's extent
    // model + watcher unification).
    this.watchCandidateBoxes(this.candidates);
  }

  /**
   * Box-watch EVERY scroller pane, not just the mirroring targets: a pane
   * that fits at scan time may start overflowing later (window resize,
   * content growth) without any DOM mutation — only its own box change
   * wakes the rescan, so it must be observed too. (Candidate panes that
   * are replaced by a board re-render are caught by the MutationObserver.)
   */
  private watchCandidateBoxes(candidates: HTMLElement[]): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    // One observer for the component's lifetime. Re-observing an element
    // makes ResizeObserver report its initial size again, which would
    // schedule another scan, which would re-observe… an endless 60 ms
    // cycle (CR db66a9fe) — so apply only the observe/unobserve diff.
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleScan());
    }
    const next = new Set(candidates);
    for (const el of next) {
      if (!this.observedBoxes.has(el)) {
        this.resizeObserver.observe(el);
      }
    }
    for (const el of this.observedBoxes) {
      if (!next.has(el)) {
        this.resizeObserver.unobserve(el);
      }
    }
    this.observedBoxes = next;
  }

  /** Target scrolled (or echoed our own write) — mirror into the strip. */
  private onTargetScroll(): void {
    if (this.syncingFromBar) {
      return;
    }
    const left = Math.max(...this.targets.map((el) => el.scrollLeft));
    if (this.track.nativeElement.scrollLeft !== left) {
      this.track.nativeElement.scrollLeft = left;
    }
    this.cd.detectChanges();
  }

  /**
   * The strip scrolled (native overflow track) — push to every target. The
   * targets' scroll events echo back while the guard holds, so no loop.
   */
  onTrackScroll(): void {
    const left = this.track.nativeElement.scrollLeft;
    this.syncingFromBar = true;
    for (const el of this.targets) {
      if (el.scrollLeft !== left) {
        el.scrollLeft = left;
      }
    }
    this.cd.detectChanges();
    if (this.echoTimer !== null) {
      clearTimeout(this.echoTimer);
    }
    this.echoTimer = setTimeout(() => {
      this.syncingFromBar = false;
      this.echoTimer = null;
    }, 120);
  }

  /** Vertical wheel over the strip scrolls the board horizontally. */
  onWheel(event: WheelEvent): void {
    if (!this.visible) {
      return;
    }
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      this.track.nativeElement.scrollLeft += event.deltaY;
      this.onTrackScroll();
    }
  }

  /** Click on the track (not the thumb) seeks — centered on the click. */
  onTrackClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList?.contains('sticky-scrollbar-thumb')) {
      return;
    }
    const rect = this.track.nativeElement.getBoundingClientRect();
    const visibleWidth = this.trackViewWidth();
    const contentX = event.clientX - rect.left + this.trackScrollLeft();
    this.track.nativeElement.scrollLeft = Math.max(
      0,
      Math.min(this.contentWidth - visibleWidth, contentX - visibleWidth / 2),
    );
    this.onTrackScroll();
  }

  /** Thumb drag: pointer moves 1:1 with the track viewport. */
  onThumbPointerDown(event: PointerEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startScroll = this.trackScrollLeft();
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const visibleWidth = this.trackViewWidth();
      // The thumb only travels the unscrolled fraction of the track over the
      // full content range (CR 6c80260f): scale pointer movement by
      // maxScroll / thumbTravel so a full-width thumb drag reaches the scroll
      // limit — matching the native bar's drag behaviour. Degenerate cases
      // (no travel, no viewport) fall back to the unscaled 1:1 mapping.
      const max = Math.max(0, this.contentWidth - visibleWidth);
      const ratio = Math.min(1, visibleWidth > 0 ? visibleWidth / Math.max(1, this.contentWidth) : 1);
      const thumbTravel = visibleWidth - visibleWidth * ratio;
      const scale = thumbTravel > 0 ? max / thumbTravel : 1;
      const next = startScroll + dx * scale;
      this.track.nativeElement.scrollLeft = Math.max(0, Math.min(max, next));
      this.onTrackScroll();
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    this.zone.runOutsideAngular(() => {
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    });
  }
}